import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BookOpen } from 'lucide-react';
import { ModuleId } from '../../types';
import { SubSkillCheatSheet } from './SubSkillCheatSheet';
interface CheatSheetDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  moduleId: ModuleId;
  subSkill?: string;
}
/**
 * Bottom sheet drawer for the Spiekbriefje (cheat sheet / hint system).
 * Slides up from the bottom — feels natural on mobile, less intrusive on desktop.
 */
export function CheatSheetDrawer({
  isOpen,
  onClose,
  moduleId,
  subSkill
}: CheatSheetDrawerProps) {
  // Escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  return <AnimatePresence data-id="element-1157">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40" data-id="element-1158" />

          {/* Bottom Sheet */}
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 32,
        stiffness: 350
      }} className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl max-h-[75vh] flex flex-col" data-id="element-1159">
            {/* Drag Handle */}
            <div className="flex justify-center pt-3 pb-1" data-id="element-1160">
              <div className="w-10 h-1 rounded-full bg-slate-300" data-id="element-1161" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-3 border-b border-slate-100" data-id="element-1162">
              <div className="flex items-center gap-2.5" data-id="element-1163">
                <div className="bg-indigo-50 p-1.5 rounded-lg" data-id="element-1164">
                  <BookOpen className="w-5 h-5 text-indigo-600" data-id="element-1165" />
                </div>
                <div data-id="element-1166">
                  <h2 className="text-lg font-bold text-slate-900 leading-tight" data-id="element-1167">
                    Spiekbriefje
                  </h2>
                  <p className="text-[11px] text-slate-400 font-medium uppercase tracking-wider" data-id="element-1168">
                    {moduleId}
                    {subSkill ? ` · ${subSkill}` : ''}
                  </p>
                </div>
              </div>

              <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-600" aria-label="Sluiten" data-id="element-1169">
                <X className="w-5 h-5" data-id="element-1170" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4" data-id="element-1171">
              <SubSkillCheatSheet moduleId={moduleId} subSkill={subSkill} data-id="element-1172" />
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}